import { useState, useEffect } from "react";
import styles from "./AllNotificationsPage.module.css";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const AUDIENCE_LABELS = {
  all: "Everyone",
  all_users: "All Users",
  all_traders: "All Traders",
  free: "Free Users",
  paid: "Paid Users",
  per_plan: "Per Plan",
  per_user: "Specific Users",
  per_trader: "Specific Traders",
};

const SchedulesPage = () => {
  const { setEditSchedule, setIsCreateNotificationScreen } =
    useDashboardStore();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetch = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(ENDPOINTS.GET_NOTIFICATION_SCHEDULES);
      if (data.success) setItems(data.schedules || []);
    } catch (err) {
      console.error("Failed to fetch schedules", err);
      toast.error("Failed to fetch schedules");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const handleToggle = async (s) => {
    try {
      const { data } = await api.patch(
        ENDPOINTS.TOGGLE_NOTIFICATION_SCHEDULE(s.id),
        { is_active: !s.is_active },
      );
      if (data.success) fetch();
    } catch (err) {
      toast.error("Failed to update schedule");
    }
  };

  const handleEdit = (s) => {
    setEditSchedule(s);
    setIsCreateNotificationScreen();
  };

  const handleDelete = (s) => {
    confirmAlert({
      title: "Delete schedule",
      message: `Delete "${s.title}"? It will stop sending.`,
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_NOTIFICATION_SCHEDULE(s.id),
              );
              if (data.success) {
                toast.success("Schedule deleted");
                fetch();
              }
            } catch (err) {
              toast.error("Failed to delete");
            }
          },
        },
        { label: "No", onClick: () => {} },
      ],
    });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p className={styles.loadingText}>Loading schedules...</p>
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Status</th>
            <th>Title</th>
            <th>Repeat</th>
            <th>Audience</th>
            <th>Next send</th>
            <th>Sent</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className={styles.emptyCell}>
                No schedules yet.
              </td>
            </tr>
          ) : (
            items.map((s) => (
              <tr key={s.id}>
                <td>
                  <span
                    className={`${styles.sourceBadge} ${
                      s.is_active ? styles.sourceAdmin : styles.sourceSystem
                    }`}
                  >
                    {s.is_active ? "active" : "paused"}
                  </span>
                </td>
                <td className={styles.contentCell}>{s.title}</td>
                <td>{s.summary}</td>
                <td>{AUDIENCE_LABELS[s.audience_type] || s.audience_type}</td>
                <td>
                  {s.is_active && s.next_run_at ? (
                    dayjs.utc(s.next_run_at).local().format("DD-MM-YYYY HH:mm")
                  ) : (
                    <span className={styles.lifetime}>—</span>
                  )}
                </td>
                <td>
                  {s.end_type === "count"
                    ? `${s.occurrence_count}/${s.max_occurrences}`
                    : s.occurrence_count}
                </td>
                <td>
                  <div className={styles.actionsCell}>
                    <span
                      className={styles.resend}
                      onClick={() => handleToggle(s)}
                      title={s.is_active ? "Pause" : "Resume"}
                    >
                      {s.is_active ? "Pause" : "Resume"}
                    </span>
                    <span className={styles.resend} onClick={() => handleEdit(s)}>
                      Edit
                    </span>
                    <span
                      className={styles.delete}
                      onClick={() => handleDelete(s)}
                    >
                      Delete
                    </span>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SchedulesPage;
